import { forwardRef, useEffect, useRef, useState, ChangeEvent, ReactNode, Ref, useCallback, RefObject } from 'react';

// material-ui
import { styled, useTheme, Theme } from '@mui/material/styles';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  SelectChangeEvent,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

// third-party
import { CSVLink } from 'react-csv';
import { Headers } from 'react-csv/lib/core';
import { Column, ColumnInstance, HeaderGroup, Row, SortingRule, useAsyncDebounce } from 'react-table';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { useDrop, useDrag, useDragLayer, DragLayerMonitor, DropTargetMonitor } from 'react-dnd';

// assets
import { DocumentDownload, HambergerMenu, Maximize1 } from 'iconsax-react';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
// types
import { ThemeMode } from 'types/config';
import { FormattedMessage } from 'react-intl';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { Divider } from '@mui/material';
import { CircularProgress } from '@mui/material';

// ==============================|| SORT HEADER ||============================== //

interface HeaderSortProps {
  column: HeaderGroup<{}>;
  sort?: boolean;
}

export const HeaderSort = ({ column, sort }: HeaderSortProps) => {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: column.disableSortBy ? '100%' : 'max-content' }}>{column.render('Header')}</Box>
      {!column.disableSortBy && (
        <Stack
          sx={{ color: theme.palette.secondary.colorNeutrals?.neutrals50 }}
          {...(sort && { ...column.getHeaderProps(column.getSortByToggleProps()) })}
        >
          <KeyboardArrowUpOutlined
            sx={{
              fontSize: 16,
              color: column.isSorted && !column.isSortedDesc ? theme.palette.secondary.colorNeutrals?.neutrals10 : 'inherit'
            }}
          />
          <KeyboardArrowDownOutlined
            sx={{
              marginTop: -0.8,
              fontSize: 16,
              color: column.isSortedDesc ? theme.palette.secondary.colorNeutrals?.neutrals10 : 'inherit'
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};

// ==============================|| TABLE PAGINATION ||============================== //

interface TablePaginationProps {
  gotoPage: (value: number) => void;
  setPageSize: (value: number) => void;
  pageIndex: number;
  pageSize: number;
  initialPageSize?: number;
  rows: Row[];
}

export const TablePagination = ({ gotoPage, rows, setPageSize, pageSize, pageIndex, initialPageSize }: TablePaginationProps) => {
  const [open, setOpen] = useState(false);
  let options: number[] = [5, 10, 25, 50, 100];

  if (initialPageSize) {
    options = [...options, initialPageSize]
      .filter((item, index) => [...options, initialPageSize].indexOf(item) === index)
      .sort(function (a, b) {
        return a - b;
      });
  }

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleChangePagination = (event: ChangeEvent<unknown>, value: number) => {
    gotoPage(value - 1);
  };

  const handleChange = (event: SelectChangeEvent<number>) => {
    setPageSize(+event.target.value);
  };

  return (
    <Grid container alignItems="center" justifyContent="space-between" sx={{ width: 'auto' }}>
      <Grid item>
        <Stack direction="row" spacing={1} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="secondary">
              <FormattedMessage id="elements.tables.rowsPerPage" />
            </Typography>
            <FormControl sx={{ m: 1 }}>
              <Select
                id="demo-controlled-open-select"
                open={open}
                onClose={handleClose}
                onOpen={handleOpen}
                value={pageSize}
                onChange={handleChange}
                size="small"
                sx={{ '& .MuiSelect-select': { py: 0.75, px: 1.25 } }}
              >
                {options.map((option: number) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="caption" color="secondary">
            Go to
          </Typography>
          <TextField
            size="small"
            type="number"
            value={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) : 0;
              gotoPage(page - 1);
            }}
            sx={{ '& .MuiOutlinedInput-input': { py: 0.75, px: 1.25, width: 36 } }}
          />
        </Stack>
      </Grid>
      <Grid item sx={{ mt: { xs: 2, sm: 0 } }}>
        <Pagination
          count={Math.ceil(rows.length / pageSize)}
          page={pageIndex + 1}
          onChange={handleChangePagination}
          color="primary"
          variant="combined"
          showFirstButton
          showLastButton
        />
      </Grid>
    </Grid>
  );
};

// ==============================|| SELECTION - PREVIEW ||============================== //

export const IndeterminateCheckbox = forwardRef(
  (
    {
      indeterminate = false,
      showCheckboxInSubRows = true,
      isMainRow = false,
      disabled = false,
      ...rest
    }: {
      indeterminate?: boolean;
      showCheckboxInSubRows?: boolean | undefined;
      isMainRow?: boolean | undefined;
      onChange: any;
      disabled?: boolean;
    },
    ref: Ref<any>
  ) => {
    const defaultRef = useRef();
    const resolvedRef = ref || defaultRef;

    return isMainRow || (showCheckboxInSubRows && !isMainRow) ? (
      <Checkbox indeterminate={indeterminate} ref={resolvedRef} disabled={disabled} {...rest} />
    ) : (
      <></>
    );
  }
);

export const TableRowSelection = ({ selected }: { selected: number }) => (
  <>
    {selected > 0 && (
      <Chip
        size="small"
        label={`${selected} row(s) selected`}
        color="secondary"
        variant="light"
        sx={{
          /* position: 'absolute', */
          right: -1,
          top: -1,
          borderRadius: '0 4px 0 4px'
        }}
      />
    )}
  </>
);

// ==============================|| DRAG & DROP - DRAGGABLE HEADR ||============================== //

export interface Item {
  header: string;
  id: string;
  index: number;
}

interface DraggableHeaderProps {
  column: Column;
  reorder: (item: Item, index: number) => void;
  index: number;
  children: ReactNode;
}

export const DraggableHeader = ({ children, column, index, reorder }: DraggableHeaderProps) => {
  const theme = useTheme();
  const ref = useRef();
  const { id, Header } = column;

  const DND_ITEM_TYPE = 'column';

  const [{ isOverCurrent }, drop] = useDrop({
    accept: DND_ITEM_TYPE,
    drop: (item: Item) => {
      reorder(item, index);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true })
    })
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_ITEM_TYPE,
    item: () => ({
      id,
      index,
      header: Header
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  drag(drop(ref));

  let borderColor = theme.palette.text.primary;
  if (isOverCurrent) {
    borderColor = theme.palette.primary.main;
  }

  return (
    <Box sx={{ cursor: 'move', opacity: isDragging ? 0.5 : 1, color: borderColor }} ref={ref}>
      {children}
    </Box>
  );
};

// ==============================|| DRAG & DROP - DRAG PREVIEW ||============================== //

const DragHeader = styled('div')(({ theme, x, y }: { theme: Theme; x: number; y: number }) => ({
  color: theme.palette.text.secondary,
  position: 'fixed',
  pointerEvents: 'none',
  left: 12,
  top: 24,
  transform: `translate(${x}px, ${y}px)`,
  opacity: 0.6
}));

export const DragPreview = () => {
  const theme = useTheme();

  const { isDragging, item, currentOffset } = useDragLayer((monitor: DragLayerMonitor<{}>) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging()
  }));

  const { x, y } = currentOffset || {};

  return isDragging ? (
    <DragHeader theme={theme} x={x!} y={y!}>
      {item.header && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Maximize1 style={{ fontSize: '1rem' }} />
          <Typography>{item.header}</Typography>
        </Stack>
      )}
    </DragHeader>
  ) : null;
};

// ==============================|| DRAG & DROP - DRAGGABLE ROW ||============================== //

interface DraggableRowProps {
  index: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
  children: ReactNode;
}

export const DraggableRow = ({ index, moveRow, children }: DraggableRowProps) => {
  const DND_ITEM_TYPE = 'row';

  const dropRef = useRef<any>(null);
  const dragRef = useRef(null);

  const [, drop] = useDrop({
    accept: DND_ITEM_TYPE,
    hover(item: any, monitor: DropTargetMonitor<{}>) {
      if (!dropRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = dropRef.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  const opacity = isDragging ? 0 : 1;

  preview(drop(dropRef));
  drag(dragRef);

  return (
    <TableRow ref={dropRef} style={{ opacity, backgroundColor: isDragging ? 'red' : 'inherit' }}>
      <TableCell size="small" ref={dragRef} sx={{ cursor: 'move', textAlign: 'center', width: '20px' }}>
        <Tooltip placement="top" arrow title={'Click and drag up or down to change field order'}>
          <HambergerMenu size={16} />
        </Tooltip>
      </TableCell>
      {children}
    </TableRow>
  );
};

// ==============================|| COLUMN HIDING - SELECT ||============================== //

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 200
    }
  }
};

interface HidingSelectProps {
  allColumns: ColumnInstance<{}>[];
  hiddenColumns: string[];
  setHiddenColumns: (param: string[]) => void;
}

export const HidingSelect = ({ hiddenColumns, setHiddenColumns, allColumns }: HidingSelectProps) => {
  const handleChange = (event: SelectChangeEvent<typeof hiddenColumns>) => {
    const {
      target: { value }
    } = event;

    setHiddenColumns(typeof value === 'string' ? value.split(',') : value!);
  };

  let visible = allColumns.filter((c: ColumnInstance) => !hiddenColumns.includes(c.id)).length;

  return (
    <FormControl sx={{ width: 200 }}>
      <Select
        id="column-hiding"
        multiple
        displayEmpty
        value={hiddenColumns}
        onChange={handleChange}
        input={<OutlinedInput id="select-column-hiding" placeholder="select column" />}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return <Typography variant="subtitle1">All columns visible</Typography>;
          }

          if (selected.length > 0 && selected.length === allColumns.length) {
            return <Typography variant="subtitle1">All columns visible</Typography>;
          }

          return <Typography variant="subtitle1">{visible} column(s) visible</Typography>;
        }}
        MenuProps={MenuProps}
        size="small"
      >
        {allColumns.map((column: ColumnInstance) => {
          let ToggleChecked = column.id === '#' ? true : hiddenColumns!.indexOf(column.id) > -1 ? false : true;
          return (
            <MenuItem key={column.id} value={column.id} sx={{ '&.Mui-selected': { bgcolor: 'background.paper' } }}>
              <Checkbox checked={ToggleChecked} color="success" />
              <ListItemText primary={typeof column.Header === 'string' ? column.Header : column?.title} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

// ==============================|| COLUMN SORTING - SELECT ||============================== //

interface SortingSelectProps {
  sortBy: string;
  setSortBy: (sortBy: SortingRule<{}>[]) => void;
  allColumns: ColumnInstance<{}>[];
}

export const SortingSelect = ({ sortBy, setSortBy, allColumns }: SortingSelectProps) => {
  const [sort, setSort] = useState<string>(sortBy);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const {
      target: { value }
    } = event;
    setSort(value);
    setSortBy([{ id: value, desc: false }]);
  };

  return (
    <FormControl sx={{ width: 200 }}>
      <Select
        id="column-hiding"
        displayEmpty
        value={sort}
        onChange={handleChange}
        input={<OutlinedInput id="select-column-hiding" placeholder="Sort by" />}
        renderValue={(selected) => {
          const selectedColumn = allColumns.filter((column: ColumnInstance) => column.id === selected)[0];
          if (!selected) {
            return <Typography variant="subtitle1">Sort By</Typography>;
          }

          return (
            <Typography variant="subtitle2">
              Sort by ({typeof selectedColumn.Header === 'string' ? selectedColumn.Header : selectedColumn?.title})
            </Typography>
          );
        }}
      >
        {allColumns
          .filter((column: ColumnInstance) => column.canSort)
          .map((column: ColumnInstance) => (
            <MenuItem key={column.id} value={column.id}>
              <ListItemText primary={typeof column.Header === 'string' ? column.Header : column?.title} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};

// ==============================|| CSV EXPORT ||============================== //

interface CSVExportProps {
  data: never[] | any[];
  filename: string;
  headers?: Headers;
}

export const CSVExport = ({ data, filename, headers }: CSVExportProps) => {
  return (
    <CSVLink data={data} filename={filename} headers={headers}>
      <Tooltip title="CSV Export">
        <DocumentDownload size={28} style={{ color: 'gray', marginTop: 4 }} />
      </Tooltip>
    </CSVLink>
  );
};

// ==============================|| EMPTY TABLE - NO DATA  ||============================== //

const StyledGridOverlay = styled(Stack)(({ theme }) => ({
  height: '300px',
  '& .ant-empty-img-1': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary[200] : theme.palette.secondary[400]
  },
  '& .ant-empty-img-2': {
    fill: theme.palette.secondary.light
  },
  '& .ant-empty-img-3': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary[400] : theme.palette.secondary[200]
  },
  '& .ant-empty-img-4': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary.dark : theme.palette.common.white
  },
  '& .ant-empty-img-5': {
    fillOpacity: theme.palette.mode === ThemeMode.DARK ? '0.09' : '0.95',
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary.darker : theme.palette.secondary.light
  }
}));

export const EmptyTable = ({ msg, colSpan }: { msg: string; colSpan?: number }) => {
  return (
    <TableRow>
      <TableCell colSpan={colSpan}>
        <StyledGridOverlay alignItems="center" justifyContent="center" spacing={1}>
          <svg width="150" height="129.612" viewBox="0 0 150 129.612" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M148.288 84.005c-4.069 -20.476 -27.906 -36.839 -32.556 -58.175C107.282 -1.316 79.43 -5.607 61.331 6.182 32.653 24.862 63.246 29.497 7.019 73.694c-13.885 10.913 -9.46 59.083 34.219 48.029 23.505 -5.95 30.651 -6.785 41.224 -7.765 26.651 -2.468 72.657 4.415 65.827 -29.954"
              fill="#FFDAB2"
            />
            <g clip-path="url(#clip0_3418_13003)">
              <path d="m116.453 94.736 -41.19 21.71 -41.126 -21.71V61.954H116.453z" fill="white" />
              <path
                d="M75.262 116.777a0.3 0.3 0 0 1 -0.156 -0.038l-41.126 -21.71a0.338 0.338 0 0 1 -0.178 -0.292V61.956a0.335 0.335 0 0 1 0.334 -0.335H116.453a0.335 0.335 0 0 1 0.334 0.335v32.782a0.338 0.338 0 0 1 -0.179 0.291l-41.189 21.709a0.3 0.3 0 0 1 -0.156 0.039m-40.794 -22.24 40.795 21.531 40.855 -21.532V62.287H34.468z"
                fill="#5B5B5B"
              />
              <path d="m116.453 54.92 -41.189 -21.709 18.51 -8.902 41.188 21.71z" fill="white" />
              <path
                d="M116.453 55.252a0.338 0.338 0 0 1 -0.156 -0.038l-41.19 -21.708a0.334 0.334 0 0 1 0.012 -0.596l18.51 -8.902a0.338 0.338 0 0 1 0.3 0.006l41.188 21.709a0.335 0.335 0 0 1 0.125 0.473 0.338 0.338 0 0 1 -0.136 0.121l-18.51 8.902a0.3 0.3 0 0 1 -0.146 0.034m-40.447 -22.028 40.456 21.323 17.761 -8.544 -40.456 -21.32z"
                fill="#5B5B5B"
              />
              <path d="m34.135 54.92 41.188 -21.71 -18.51 -8.902 -41.187 21.711z" fill="white" />
              <path
                d="M34.135 55.252a0.3 0.3 0 0 1 -0.146 -0.032l-18.507 -8.902a0.335 0.335 0 0 1 -0.012 -0.594l41.191 -21.71a0.338 0.338 0 0 1 0.3 -0.006l18.51 8.902a0.334 0.334 0 0 1 0.011 0.596l-41.19 21.708a0.338 0.338 0 0 1 -0.158 0.039m-17.768 -9.248 17.761 8.543 40.456 -21.322L56.822 24.682z"
                fill="#5B5B5B"
              />
              <path d="m34.135 54.92 41.188 21.71 -21.178 11.564 -41.19 -21.707z" fill="white" />
              <path
                d="M54.145 88.529a0.3 0.3 0 0 1 -0.156 -0.039L12.801 66.781a0.332 0.332 0 0 1 -0.005 -0.586l21.178 -11.567a0.338 0.338 0 0 1 0.316 -0.002l41.191 21.71a0.335 0.335 0 0 1 0.131 0.462 0.338 0.338 0 0 1 -0.127 0.124l-21.179 11.566a0.338 0.338 0 0 1 -0.161 0.041m-40.485 -22.047 40.483 21.337 20.476 -11.183 -40.481 -21.335z"
                fill="#5B5B5B"
              />
              <path d="m116.451 54.92 -41.188 21.71 21.178 11.566 41.19 -21.71z" fill="white" />
              <path
                d="M96.44 88.529a0.338 0.338 0 0 1 -0.161 -0.042l-21.177 -11.565a0.332 0.332 0 0 1 0.005 -0.586l41.188 -21.71a0.338 0.338 0 0 1 0.316 0.003L137.79 66.195a0.334 0.334 0 0 1 -0.005 0.586l-41.19 21.709a0.3 0.3 0 0 1 -0.156 0.038m-20.473 -11.895 20.476 11.185 40.484 -21.336 -20.476 -11.185z"
                fill="#5B5B5B"
              />
              <path d="M75.325 33.212 34.136 54.92l41.126 21.71 41.19 -21.71z" fill="#252523" />
              <path d="M75.597 76.63h-0.668v39.815h0.667z" fill="#5B5B5B" />
              <path
                d="M52.184 52.95a0.262 0.262 0 0 1 -0.262 -0.223 0.262 0.262 0 0 1 0.044 -0.198c0.051 -0.07 4.874 -7.03 -0.51 -13.375a10.147 10.147 0 0 0 -7.768 -3.6h-0.073c0.512 1.774 0.422 3.349 -0.303 4.559a3.375 3.375 0 0 1 -3.118 1.712 2.677 2.677 0 0 1 -2.315 -1.799c-0.594 -1.542 0.025 -3.037 1.657 -4a7.875 7.875 0 0 1 3.36 -0.964 14.1 14.1 0 0 0 -1.486 -2.884c-1.944 -2.963 -5.489 -5.014 -9.724 -5.627 -3.588 -0.52 -7.028 0.091 -8.764 1.558a0.266 0.266 0 0 1 -0.345 -0.408c1.847 -1.558 5.452 -2.219 9.187 -1.676 4.387 0.635 8.065 2.771 10.092 5.863a14.55 14.55 0 0 1 1.595 3.147 10.688 10.688 0 0 1 8.411 3.778c5.658 6.664 0.591 13.953 0.538 14.026a0.262 0.262 0 0 1 -0.215 0.112M43.07 35.576a7.425 7.425 0 0 0 -3.261 0.909c-1.408 0.832 -1.93 2.054 -1.432 3.351a2.152 2.152 0 0 0 1.864 1.46 2.857 2.857 0 0 0 2.614 -1.454c0.664 -1.111 0.725 -2.587 0.214 -4.267"
                fill="#3D3D3B"
              />
              <path
                d="M18.316 32.403a1.82 1.82 0 0 1 -2.248 -1.168 1.811 1.811 0 0 1 1.11 -2.275 1.82 1.82 0 0 1 2.308 1.047 1.811 1.811 0 0 1 -1.169 2.396m-0.888 -2.96a1.277 1.277 0 1 0 1.519 1.78 1.274 1.274 0 0 0 -1.519 -1.78"
                fill="#3D3D3B"
              />
              <path
                d="M18.313 28.229a1.82 1.82 0 0 1 -2.193 -1.034 1.806 1.806 0 0 1 1.311 -2.476 1.823 1.823 0 0 1 1.867 0.762 1.806 1.806 0 0 1 -0.985 2.748m-0.887 -2.961a1.279 1.279 0 1 0 1.526 1.771 1.277 1.277 0 0 0 -1.526 -1.771"
                fill="#3D3D3B"
              />
              <path
                d="M17.796 30.113c1.311 0 2.374 -0.71 2.374 -1.583s-1.063 -1.582 -2.374 -1.582 -2.374 0.708 -2.374 1.582 1.063 1.582 2.374 1.582"
                fill="white"
              />
              <path
                d="M18.845 30.23a3.675 3.675 0 0 1 -1.048 0.15c-1.48 0 -2.641 -0.81 -2.641 -1.847s1.158 -1.85 2.638 -1.85a3.353 3.353 0 0 1 1.828 0.506 1.515 1.515 0 0 1 0.597 2.123 1.5 1.5 0 0 1 -0.596 0.556 2.925 2.925 0 0 1 -0.776 0.36m-1.937 -2.888c-0.713 0.212 -1.219 0.673 -1.219 1.19 0 0.713 0.965 1.315 2.109 1.313a2.775 2.775 0 0 0 1.529 -0.419 0.988 0.988 0 0 0 0 -1.794 2.775 2.775 0 0 0 -1.532 -0.418 3 3 0 0 0 -0.888 0.128"
                fill="#3D3D3B"
              />
              <path d="m18.379 30.064 -0.058 -3.076 -0.766 -0.031 -0.053 3.143z" fill="#3D3D3B" />
              <path d="M14.646 29.199a0.244 0.244 0 1 0 0 -0.488 0.244 0.244 0 0 0 0 0.489" fill="#3D3D3B" />
              <path d="M14.838 27.892a0.244 0.244 0 1 0 0 -0.491 0.244 0.244 0 0 0 0 0.491" fill="#3D3D3B" />
              <path
                d="M94.124 37.588a0.262 0.262 0 0 1 -0.255 -0.188 26.362 26.362 0 0 1 -0.696 -10.127c0.69 -4.954 3.428 -11.457 12.553 -14.304q1.93 -0.619 3.938 -0.9c0.43 -2.68 2.065 -5.588 5.723 -7.593 8.691 -4.766 14.563 -0.489 14.621 -0.446a0.266 0.266 0 1 1 -0.32 0.425c-0.055 -0.041 -5.666 -4.107 -14.043 0.488 -3.418 1.874 -4.984 4.564 -5.428 7.063 4.024 -0.396 5.952 0.944 6.817 1.954a4.125 4.125 0 0 1 0.617 4.512 3.218 3.218 0 0 1 -3.161 1.841c-2.247 -0.131 -4.107 -2.018 -4.738 -4.808a9 9 0 0 1 -0.163 -2.876 22.5 22.5 0 0 0 -3.705 0.849c-7.112 2.219 -11.211 6.886 -12.185 13.868a25.8 25.8 0 0 0 0.677 9.899 0.262 0.262 0 0 1 -0.134 0.316 0.3 0.3 0 0 1 -0.121 0.029zm16.011 -25.024a8.475 8.475 0 0 0 0.141 2.825c0.577 2.551 2.244 4.276 4.248 4.393a2.723 2.723 0 0 0 2.651 -1.545 3.585 3.585 0 0 0 -0.546 -3.929c-1.248 -1.461 -3.526 -2.052 -6.493 -1.744"
                fill="#3D3D3B"
              />
              <path
                d="M136.228 6.721a1.905 1.905 0 0 1 -1.718 -1.084 1.899 1.899 0 0 1 1.105 -2.618 1.908 1.908 0 0 1 2.48 1.395 1.902 1.902 0 0 1 -1.044 2.12 1.95 1.95 0 0 1 -0.823 0.187m0.003 -3.272a1.369 1.369 0 0 0 -1.293 0.914 1.365 1.365 0 0 0 1.209 1.822 1.375 1.375 0 0 0 1.427 -1.657 1.361 1.361 0 0 0 -1.343 -1.078"
                fill="#3D3D3B"
              />
              <path
                d="M135.422 11.068a1.912 1.912 0 0 1 -1.908 -1.806 1.908 1.908 0 0 1 1.704 -1.998 1.915 1.915 0 0 1 2.091 1.59 1.905 1.905 0 0 1 -1.478 2.17q-0.204 0.044 -0.408 0.044m0.007 -3.272a1.365 1.365 0 0 0 -1.347 1.582 1.362 1.362 0 0 0 1.492 1.135 1.369 1.369 0 0 0 1.216 -1.427 1.361 1.361 0 0 0 -1.362 -1.288"
                fill="#3D3D3B"
              />
              <path
                d="M138.291 7.5c0.167 -0.91 -0.804 -1.853 -2.168 -2.103 -1.364 -0.252 -2.607 0.283 -2.776 1.193 -0.169 0.909 0.801 1.85 2.167 2.102s2.607 -0.283 2.776 -1.191"
                fill="white"
              />
              <path
                d="M136.186 9.02a4.5 4.5 0 0 1 -0.721 -0.067 3.495 3.495 0 0 1 -1.796 -0.874 1.703 1.703 0 0 1 -0.586 -1.537 1.703 1.703 0 0 1 1.097 -1.229 3.728 3.728 0 0 1 3.787 0.699 1.703 1.703 0 0 1 0.586 1.537 1.703 1.703 0 0 1 -1.097 1.228 3.225 3.225 0 0 1 -1.27 0.244m-0.737 -3.419a2.7 2.7 0 0 0 -1.057 0.201 1.208 1.208 0 0 0 -0.782 0.836 1.193 1.193 0 0 0 0.431 1.059 3.202 3.202 0 0 0 3.204 0.59 1.208 1.208 0 0 0 0.782 -0.835 1.193 1.193 0 0 0 -0.433 -1.059 2.955 2.955 0 0 0 -1.52 -0.734 3.525 3.525 0 0 0 -0.623 -0.058z"
                fill="#3D3D3B"
              />
              <path d="m135.51 5.336 -0.536 3.214 0.79 0.179 0.665 -3.262z" fill="#3D3D3B" />
              <path d="M138.728 8.793a0.259 0.259 0 1 0 0 -0.518 0.259 0.259 0 0 0 0 0.518" fill="#3D3D3B" />
              <path d="M139.18 7.468a0.259 0.259 0 1 0 0 -0.518 0.259 0.259 0 0 0 0 0.518" fill="#3D3D3B" />
            </g>
            <defs>
              <clipPath id="clip0_3418_13003">
                <path width="102.293" height="87.0255" fill="white" d="M0 1.145h148.97v126.737H0z" />
              </clipPath>
            </defs>
          </svg>
          <Typography align="center" color="secondary">
            {msg}
          </Typography>
        </StyledGridOverlay>
      </TableCell>
    </TableRow>
  );
};

export const ColumnFilterInputBox = ({ column: { filterValue, setFilter } }: { column: HeaderGroup<{}> }) => {
  const [filterVal, setFilterVal] = useState<string>('');
  const theme = useTheme();

  const setFilterWithDebounce = useAsyncDebounce((value) => {
    setFilter(value || undefined);
  }, 300);

  const onChange = useCallback(
    (e: any) => {
      const value = e.target.value;
      setFilterVal(value);
      setFilterWithDebounce(value);
    },
    [setFilterWithDebounce]
  );

  useEffect(() => {
    setFilterVal(filterValue);
  }, [filterValue]);

  return (
    <TextField
      inputProps={{
        style: {
          backgroundColor: theme.palette.common.white
        }
      }}
      onChange={onChange}
      value={filterVal}
      placeholder={'Search'}
      size={'small'}
    />
  );
};

const customMargin = 20;
export const FloatDeleteButton = ({
  totalSelectedRows,
  tableContainerRef,
  floatButtonColumnNumber,
  onClose,
  onClickMultiDelete,
  selectedRows = [],
  isSelectedAll = false
}: {
  totalSelectedRows: number;
  tableContainerRef: RefObject<HTMLDivElement>;
  floatButtonColumnNumber: number;
  onClose: () => void;
  onClickMultiDelete?: (_data: any[]) => Promise<any>;
  selectedRows?: any[];
  isSelectedAll?: boolean;
}) => {
  const theme = useTheme();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!tableContainerRef.current || !buttonRef.current || !floatButtonColumnNumber) return;
    const columnElement = tableContainerRef.current.querySelector(`table tbody tr:first-child td:nth-child(${floatButtonColumnNumber})`);
    if (columnElement) {
      const tableRect = tableContainerRef.current.getBoundingClientRect();
      const columnRect = columnElement.getBoundingClientRect();
      let marginLeft = Math.abs(tableRect.right - columnRect.right + customMargin);
      if (marginLeft > 0) {
        marginLeft *= -1;
      }
      buttonRef.current.style['marginLeft'] = `${marginLeft}px`;

      if (!isSelectedAll) {
        buttonRef.current.style['top'] = 'unset';
        buttonRef.current.style['transform'] = 'translateY(-50%)';
      }
    }
  }, [tableContainerRef, buttonRef, floatButtonColumnNumber, isSelectedAll]);

  const onClickDelete = async () => {
    try {
      if (onClickMultiDelete) {
        setIsDeleting(true);
        await onClickMultiDelete(selectedRows);
        onClose();
      }
    } catch (e) {
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Stack ref={buttonRef} gap={2} alignItems="center" direction="row" className="table-float-button-container">
      <Box>
        <IconButton size="small" className="close-button" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography className={'count-text'}>
        <Chip className="count-chip" label={totalSelectedRows} /> Item selected
      </Typography>
      <Divider orientation="vertical" flexItem />
      {isDeleting ? (
        <CircularProgress size={24} color="primary" />
      ) : (
        <IconButton className="delete-button" onClick={onClickDelete} sx={{ color: theme.palette.common.black }}>
          <DeleteIcon color="primary" /> <span>Delete</span>
        </IconButton>
      )}
    </Stack>
  );
};
